import { FC, Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import {
  faCheck,
  faChevronDown,
} from '@awesome.me/kit-e270131d0a/icons/classic/regular'
import { Icon, Text } from '@saasalerts/ui'
import { classNames } from '@/utils/methods'

interface SelectOption {
  id: string
  name: string
  renderElement: () => React.ReactElement
}

interface SelectWithIconProps {
  label: string
  selectedOption: SelectOption
  options: SelectOption[]
  onChange: (selectedOption: SelectOption) => void
}

export const SelectWithIcon: FC<
  React.PropsWithChildren<SelectWithIconProps>
> = ({ label, selectedOption, options, onChange }) => {
  return (
    <Listbox value={selectedOption} onChange={onChange}>
      {({ open }) => (
        <div>
          <Listbox.Label className="sui-text-blue-1000 dark:sui-text-gray-50 block text-sm leading-[22px]">
            {label}
          </Listbox.Label>
          <div className="relative mt-1">
            <Listbox.Button
              className={`dark:text-dark-text relative flex w-full cursor-default items-center justify-between rounded border border-gray-400 bg-white py-[5px] pl-3 pr-9 text-left hover:border-gray-500 dark:border-gray-800 dark:bg-dark-surface dark:hover:border-gray-600 ${
                open
                  ? 'border-brand-500 hover:border-brand-500 dark:border-brand-400 dark:hover:border-brand-400'
                  : ''
              }`}
            >
              <span className="flex h-6 w-full min-w-0 items-center rounded-full">
                {selectedOption.renderElement()}
                <Text as="span" className="ml-3 block flex-1 truncate">
                  {selectedOption.name}
                </Text>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                <Icon
                  icon={faChevronDown}
                  color="primary"
                  className={`transition-transform ${open ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                static
                className="absolute z-10 mt-1 max-h-44 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-dark-surface sm:text-sm"
              >
                {options.map((option) => (
                  <Listbox.Option
                    key={option.id}
                    className={({ selected, active }) =>
                      classNames(
                        selected
                          ? 'bg-brand-600'
                          : active
                          ? 'bg-gray-50 dark:bg-dark-activeBg'
                          : 'bg-white dark:bg-dark-surface',
                        'cursor-default select-none relative py-[5px] pl-3 pr-9'
                      )
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className="flex items-center">
                          {option.renderElement()}
                          <span
                            className={classNames(
                              selected ? 'font-semibold' : 'font-normal',
                              selected
                                ? 'text-white dark:text-gray-50'
                                : 'text-gray-900 dark:text-gray-300',
                              'ml-3 block truncate'
                            )}
                          >
                            {option.name}
                          </span>
                        </div>

                        {selected ? (
                          <span
                            className={classNames(
                              'text-white dark:text-gray-400',
                              'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}
                          >
                            <Icon
                              icon={faCheck}
                              className="size-4"
                              aria-hidden="true"
                            />
                          </span>
                        ) : (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-brand-600">
                            {/* keep space for alignment when not selected */}
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </div>
      )}
    </Listbox>
  )
}
